import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const RevenueTrends = ({ data }) => {
    if (!data || !data.daily || data.daily.length === 0) return null;

    const labels = data.daily.map(d => {
        const date = new Date(d.date + 'T00:00:00');
        return date.getDate().toString();
    });
    const revenueValues = data.daily.map(d => d.revenue);
    const orderValues = data.daily.map(d => d.orders);

    const chartData = {
        labels,
        datasets: [
            {
                label: 'Revenue (KWD)',
                data: revenueValues,
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(59,130,246,0.1)',
                fill: true,
                tension: 0.3,
                yAxisID: 'y',
            },
            {
                label: 'Orders',
                data: orderValues,
                borderColor: '#10B981',
                backgroundColor: 'rgba(16,185,129,0.1)',
                fill: true,
                tension: 0.3,
                yAxisID: 'y1',
            },
        ],
    };

    const options = {
        responsive: true,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: { position: 'top' },
            title: { display: false },
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Revenue (KWD)' },
                grid: { drawOnChartArea: true },
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: 'Orders' },
                grid: { drawOnChartArea: false },
            },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Daily Revenue & Order Trend</h6>
                <Line data={chartData} options={options} />
                <div className="row mt-3 text-center g-2">
                    <div className="col-3">
                        <small className="text-muted d-block">Month Total</small>
                        <strong>{data.totals?.current_month?.toLocaleString(undefined, { minimumFractionDigits: 3 })}</strong>
                    </div>
                    <div className="col-3">
                        <small className="text-muted d-block">vs Last Month</small>
                        <strong className={data.totals?.monthly_change_pct >= 0 ? 'text-success' : 'text-danger'}>
                            {data.totals?.monthly_change_pct >= 0 ? '+' : ''}{data.totals?.monthly_change_pct}%
                        </strong>
                    </div>
                    <div className="col-3">
                        <small className="text-muted d-block">vs Last Year</small>
                        <strong className={data.totals?.yoy_change_pct >= 0 ? 'text-success' : 'text-danger'}>
                            {data.totals?.yoy_change_pct >= 0 ? '+' : ''}{data.totals?.yoy_change_pct}%
                        </strong>
                    </div>
                    <div className="col-3">
                        <small className="text-muted d-block">Avg Daily</small>
                        <strong>{data.avgDailyRevenue?.toLocaleString(undefined, { minimumFractionDigits: 3 })}</strong>
                    </div>
                </div>
            </div>
        </div>
    );
};

const RevenueByLocation = ({ data }) => {
    if (!data || data.length === 0) return null;

    const labels = data.map(d => d.location_name);
    const values = data.map(d => d.revenue);
    const colors = labels.map((_, i) => COLORS[i % COLORS.length]);

    const chartData = {
        labels,
        datasets: [{
            label: 'Revenue (KWD)',
            data: values,
            backgroundColor: colors,
            borderRadius: 4,
        }],
    };

    const options = {
        responsive: true,
        indexAxis: 'y',
        plugins: {
            legend: { display: false },
            title: { display: false },
        },
        scales: {
            x: {
                title: { display: true, text: 'Revenue (KWD)' },
            },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Revenue by Location</h6>
                <Bar data={chartData} options={options} />
            </div>
        </div>
    );
};

const PaymentMethodChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const labels = data.map(d => `${d.label} (${d.percentage}%)`);
    const values = data.map(d => d.total_amount);
    const colors = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

    const chartData = {
        labels,
        datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8, font: { size: 11 } } },
            title: { display: false },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Payment Methods</h6>
                <Doughnut data={chartData} options={options} />
                <div className="mt-2 text-center text-muted" style={{ fontSize: '0.8rem' }}>
                    Total: {data.reduce((sum, d) => sum + d.total_amount, 0).toLocaleString(undefined, { minimumFractionDigits: 3 })} KWD
                </div>
            </div>
        </div>
    );
};

const RevenueCharts = ({ revenueTrends, revenueByLocation, revenueByPaymentMethod }) => {
    if (!revenueTrends && !revenueByLocation && !revenueByPaymentMethod) {
        return null;
    }

    return (
        <div className="mb-4">
            <h5 className="mb-3 border-bottom pb-2">Revenue & Financial</h5>
            <div className="row g-3 mb-3">
                <div className="col-12">
                    <RevenueTrends data={revenueTrends} />
                </div>
            </div>
            <div className="row g-3">
                <div className="col-md-6">
                    <RevenueByLocation data={revenueByLocation} />
                </div>
                <div className="col-md-6">
                    <PaymentMethodChart data={revenueByPaymentMethod} />
                </div>
            </div>
        </div>
    );
};

export default RevenueCharts;
